import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { useUpdateProfile } from "@/lib/inventory";
import { formatApiErrorDetail } from "@/lib/api";
import { useOverlayLock } from "@/contexts/UiOverlayContext";
import { toast } from "sonner";

const NAME_RE = /^[A-Za-z0-9_ ]{3,24}$/;

export default function DisplayNameDialog({ open, onOpenChange, currentName }) {
  const [value, setValue] = useState(currentName || "");
  const [error, setError] = useState("");
  const updateProfile = useUpdateProfile();
  useOverlayLock(open);

  useEffect(() => {
    if (open) {
      setValue(currentName || "");
      setError("");
    }
  }, [open, currentName]);

  const localValid = NAME_RE.test(value.trim());
  const liveError =
    value.length > 0 && !localValid
      ? "3–24 characters, letters/numbers/spaces/underscores only."
      : "";
  const displayedError = error || liveError;

  async function handleSave(e) {
    e.preventDefault();
    if (!localValid) {
      setError("3–24 characters, letters/numbers/spaces/underscores only.");
      return;
    }
    setError("");
    try {
      const data = await updateProfile.mutateAsync(value.trim());
      toast.success(`Display name updated to ${data.display_name}`);
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="display-name-dialog"
        className="bg-[#0A0A0A] border-white/10 text-white max-w-sm"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            Edit display name
          </DialogTitle>
          <DialogDescription className="text-[#A1A1AA] font-body text-sm">
            This is the name other collectors will see in Radar and chat.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSave} className="space-y-4 pt-2">
          <input
            data-testid="display-name-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="e.g. PaniniPro"
            maxLength={24}
            className="w-full h-11 bg-[#121212] border border-white/10 rounded-md px-3 text-white placeholder:text-white/30 font-body text-sm focus:outline-none focus:ring-1 focus:ring-[#E5B83A] focus:border-[#E5B83A]"
            autoFocus
          />
          {displayedError ? (
            <p data-testid="display-name-error" className="text-xs text-[#FF6B6B] font-body">
              {displayedError}
            </p>
          ) : (
            <p className="text-xs text-[#A1A1AA]/70 font-body">
              3–24 characters · letters, numbers, spaces, underscores.
            </p>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              data-testid="display-name-cancel"
              className="h-10 px-4 rounded-md border border-white/15 text-white font-body text-sm hover:bg-white/5 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!localValid || updateProfile.isPending}
              data-testid="display-name-save"
              className="h-10 px-4 rounded-md bg-gradient-to-r from-[#E5B83A] to-[#C9971C] text-black font-semibold text-sm hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed transition-all inline-flex items-center gap-2"
            >
              {updateProfile.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              Save
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
